/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { css as compiledCss } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ProjectIcon from '@atlaskit/icon/core/project';
import IconTile from '@atlaskit/icon/icon-tile';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Popup } from '@atlaskit/popup/popup';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import messages from './inferredProjectCardMessages';
import type { InferredProjectPerson } from './parseInferredProjectContext';
import { useInferredProjectContext } from './useInferredProjectContext';

const COMPACT_LIST_LIMIT = 3;
const HOVER_CLOSE_DELAY_MS = 150;

const projectTitleButtonStyles = compiledCss({
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.small'),
	boxDecorationBreak: 'clone',
	color: token('color.text'),
	cursor: 'pointer',
	display: 'inline',
	font: token('font.body'),
	overflowWrap: 'break-word',
	paddingBlock: '3px',
	paddingInlineEnd: '2px',
	paddingInlineStart: '2px',
	WebkitBoxDecorationBreak: 'clone',
	whiteSpace: 'normal',
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width'),
	},
	'&:hover': {
		backgroundColor: token('color.background.neutral.hovered'),
	},
});

const projectIconStyles = compiledCss({
	alignItems: 'center',
	display: 'inline-flex',
	flexShrink: 0,
	marginInlineEnd: token('space.025'),
	transform: 'translateY(-1px)',
	verticalAlign: 'middle',
});

const projectTitleStyles = compiledCss({
	minWidth: 0,
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
});

const projectLabelStyles = compiledCss({
	alignItems: 'center',
	display: 'inline-flex',
	marginInlineStart: token('space.050'),
	transform: 'translateY(-1px)',
	verticalAlign: 'middle',
});

const styles = cssMap({
	card: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		maxWidth: '360px',
		minWidth: '320px',
	},
	personRow: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	rowDivider: {
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
	},
	sectionContent: {
		paddingTop: token('space.100'),
	},
	sectionTrigger: {
		backgroundColor: 'transparent',
		paddingInline: '0',
		width: '100%',
	},
});

export type InferredProjectInlineCardProps = {
	cloudId: string;
	fallback: React.ReactNode;
	projectId: string;
	projectName: string;
};

type ProjectCardSectionProps = {
	children: React.ReactNode;
	defaultExpanded: boolean;
	title: string;
};

const CompactPersonRow = ({ person }: { person: InferredProjectPerson }): React.ReactNode => (
	<Box xcss={styles.personRow}>
		<Inline alignBlock="center" space="space.100">
			<Avatar name={person.name} size="small" />
			<Stack space="space.025">
				<Text>{person.name}</Text>
				{person.jobTitle ? <Text color="color.text.subtle">{person.jobTitle}</Text> : null}
			</Stack>
		</Inline>
	</Box>
);

const ProjectCardSection = ({
	children,
	defaultExpanded,
	title,
}: ProjectCardSectionProps): React.ReactNode => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const contentId = useId();
	const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

	return (
		<Stack space="space.050">
			<Pressable
				aria-controls={contentId}
				aria-expanded={isExpanded}
				onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
				xcss={styles.sectionTrigger}
			>
				<Inline alignBlock="center" spread="space-between">
					<Text weight="medium">{title}</Text>
					<ChevronIcon label="" color="currentColor" />
				</Inline>
			</Pressable>
			<div id={contentId}>
				{isExpanded ? <Box xcss={styles.sectionContent}>{children}</Box> : null}
			</div>
		</Stack>
	);
};

type InferredProjectCardContentProps = Omit<InferredProjectInlineCardProps, 'fallback'>;

const InferredProjectCardInner = ({
	cloudId,
	projectId,
	projectName,
}: InferredProjectCardContentProps): React.ReactNode => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const lockedByClick = useRef(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { load, ...state } = useInferredProjectContext({ cloudId, projectId });

	useEffect(
		() => () => {
			if (closeTimer.current) {
				clearTimeout(closeTimer.current);
			}
		},
		[],
	);

	const open = useCallback(() => {
		if (!isOpen) {
			load();
			setIsOpen(true);
		}
	}, [isOpen, load]);
	const closeAndUnlock = useCallback(() => {
		lockedByClick.current = false;
		setIsOpen(false);
	}, []);
	const cancelScheduledClose = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	}, []);
	const handleTriggerMouseEnter = useCallback(() => {
		cancelScheduledClose();
		open();
	}, [cancelScheduledClose, open]);
	const handleTriggerMouseLeave = useCallback(() => {
		closeTimer.current = setTimeout(() => {
			if (!lockedByClick.current) {
				closeAndUnlock();
			}
		}, HOVER_CLOSE_DELAY_MS);
	}, [closeAndUnlock]);
	const handleTriggerClick = useCallback(() => {
		if (isOpen && lockedByClick.current) {
			closeAndUnlock();
			return;
		}

		lockedByClick.current = true;
		open();
	}, [closeAndUnlock, isOpen, open]);
	const handleContentMouseEnter = useCallback(() => {
		cancelScheduledClose();
	}, [cancelScheduledClose]);
	const handleContentMouseLeave = useCallback(() => {
		if (!lockedByClick.current) {
			closeAndUnlock();
		}
	}, [closeAndUnlock]);
	const handleContentBlur = useCallback(
		(event: React.FocusEvent<HTMLElement>) => {
			if (!event.currentTarget.contains(event.relatedTarget)) {
				handleContentMouseLeave();
			}
		},
		[handleContentMouseLeave],
	);

	const context = state.status === 'success' ? state.data : undefined;
	const title = context?.title ?? projectName;
	const owner = context?.owners[0];
	const visibleCollaborators = context?.collaborators.slice(0, COMPACT_LIST_LIMIT) ?? [];
	const recentWorkUrls =
		context?.referenceIssues.flatMap((issue) => (issue.webUrl ? [issue.webUrl] : [])) ?? [];

	// Uses inline button semantics so long project names wrap within response text.
	return (
		<Popup
			isOpen={isOpen}
			onClose={closeAndUnlock}
			placement="bottom-start"
			shouldRenderToParent
			content={() => (
				<Box
					onBlur={handleContentBlur}
					onFocus={handleContentMouseEnter}
					onMouseEnter={handleContentMouseEnter}
					onMouseLeave={handleContentMouseLeave}
					padding="space.200"
					xcss={styles.card}
				>
					<Stack space="space.200">
						<Text weight="semibold">{title}</Text>
						{state.status === 'loading' ? <Text>{formatMessage(messages.loading)}</Text> : null}
						{state.status === 'error' ? <Text>{formatMessage(messages.loadError)}</Text> : null}
						{context ? (
							<Stack space="space.200">
								<ProjectCardSection defaultExpanded title={formatMessage(messages.suggestedOwner)}>
									{owner ? (
										<CompactPersonRow person={owner} />
									) : (
										<Text color="color.text.subtle">
											{formatMessage(messages.noSuggestedOwner)}
										</Text>
									)}
								</ProjectCardSection>
								<ProjectCardSection
									defaultExpanded={false}
									title={formatMessage(messages.suggestedCollaborators)}
								>
									{visibleCollaborators.length ? (
										<Stack space="space.0">
											{visibleCollaborators.map((collaborator, index) => (
												<Box
													key={collaborator.ari ?? collaborator.name}
													xcss={index > 0 ? styles.rowDivider : undefined}
												>
													<CompactPersonRow person={collaborator} />
												</Box>
											))}
										</Stack>
									) : (
										<Text color="color.text.subtle">
											{formatMessage(messages.noSuggestedCollaborators)}
										</Text>
									)}
								</ProjectCardSection>
								<ProjectCardSection
									defaultExpanded
									title={formatMessage(messages.suggestedRecentWork)}
								>
									{recentWorkUrls.length ? (
										<LinkList
											expandedMaxSources={6}
											maxSources={3}
											size="compact"
											urls={recentWorkUrls}
										/>
									) : (
										<Text color="color.text.subtle">
											{formatMessage(messages.noSuggestedRecentWork)}
										</Text>
									)}
								</ProjectCardSection>
							</Stack>
						) : null}
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => (
				// eslint-disable-next-line @atlaskit/design-system/no-html-button -- The project treatment must wrap with response text, which standard buttons cannot support.
				<span
					{...triggerProps}
					aria-label={formatMessage(messages.inferredProjectTrigger, { projectName })}
					css={projectTitleButtonStyles}
					onClick={handleTriggerClick}
					onKeyDown={(event) => {
						if (
							event.key === 'Enter' ||
							event.key === ' ' ||
							event.key === 'Space' ||
							event.key === 'Spacebar' ||
							event.code === 'Space'
						) {
							event.preventDefault();
							handleTriggerClick();
						}
					}}
					onBlur={handleTriggerMouseLeave}
					onFocus={handleTriggerMouseEnter}
					onMouseEnter={handleTriggerMouseEnter}
					onMouseLeave={handleTriggerMouseLeave}
					role="button"
					tabIndex={0}
				>
					<span>
						<span css={projectIconStyles}>
							<IconTile appearance="purple" icon={ProjectIcon} label="" size="xsmall" />
						</span>
						<span css={projectTitleStyles}>{projectName}</span>
					</span>
					<span css={projectLabelStyles}>
						<Lozenge appearance="discovery">{formatMessage(messages.inferredProject)}</Lozenge>
					</span>
				</span>
			)}
		/>
	);
};

export const InferredProjectInlineCard = ({
	fallback,
	...props
}: InferredProjectInlineCardProps): React.ReactNode => (
	<ErrorBoundary fallback={<>{fallback}</>}>
		<AtlassianRelayEnvironmentProvider>
			<InferredProjectCardInner key={`${props.cloudId}:${props.projectId}`} {...props} />
		</AtlassianRelayEnvironmentProvider>
	</ErrorBoundary>
);
